// src/pages/campaigns/forms/CampaignTargetAudienceForm.jsx
import React from 'react';
import {
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
  Box,
  Slider,
  useTheme,
  alpha,
  Divider,
  Paper,
} from '@mui/material';

// Import FormSection for consistent layout and icons
import { FormSection } from '../../../design-system/components/forms';
import {
  People as PeopleIcon,
  Public as LocationIcon,
  Language as LanguageIcon,
  Psychology as PsychologyIcon,
  Interests as InterestsIcon,
  Favorite as ValuesIcon,
  Workspaces as BehaviorIcon,
  Spa as LifestyleIcon,
  BarChart as SegmentationIcon,
  Info as InfoIcon
} from '@mui/icons-material';

// Language options
const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'ar', label: 'Arabic' },
  { value: 'fr', label: 'French' },
  { value: 'other', label: 'Other' }
];

// Gender options
const GENDER_OPTIONS = [
  { value: 'all', label: 'All Genders' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' }
];

// Common interest suggestions
const INTEREST_SUGGESTIONS = [
  'Technology', 'Travel', 'Fashion', 'Food', 'Fitness', 'Health', 'Beauty', 
  'Music', 'Movies', 'Sports', 'Gaming', 'Photography', 'Art', 'Design', 
  'Education', 'Business', 'Finance', 'Real Estate', 'Luxury', 'Family'
];

// Common behavior suggestions
const BEHAVIOR_SUGGESTIONS = [
  'Early Adopters', 'Frequent Shoppers', 'Brand Loyal', 'Price Sensitive',
  'Online Shoppers', 'Mobile Users', 'Social Media Active', 'Content Creators',
  'Research Before Purchase', 'Impulse Buyers', 'Deal Seekers', 'Eco-Conscious'
];

// Common values suggestions
const VALUES_SUGGESTIONS = [
  'Sustainability', 'Innovation', 'Tradition', 'Family', 'Authenticity',
  'Freedom', 'Security', 'Adventure', 'Luxury', 'Simplicity', 'Creativity',
  'Social Responsibility', 'Quality', 'Inclusivity', 'Wellness'
];

// Common lifestyle factor suggestions
const LIFESTYLE_SUGGESTIONS = [
  'Urban Living', 'Suburban', 'Rural', 'High Income', 'Middle Income',
  'Students', 'Parents', 'Young Professionals', 'Retirees', 'Homeowners',
  'Renters', 'Frequent Travelers', 'Outdoor Enthusiasts', 'Nightlife',
  'Health Conscious', 'Tech Savvy', 'Luxury Lifestyle'
];

// Target audience form component
const CampaignTargetAudienceForm = ({
  campaignData,
  onChange,
  onNestedChange,
  onDeepNestedChange,
  errors = {},
  touched = {}
}) => {
  const theme = useTheme();

  // Get audience data or initialize empty
  const targetAudience = campaignData.targetAudience || {
    demographics: {
      ageRange: {
        min: 18,
        max: 65
      },
      gender: 'all',
      locations: [],
      languages: []
    },
    psychographics: {
      interests: [],
      behaviors: [],
      values: [],
      lifestyleFactors: []
    },
    customSegments: []
  };

  // Handle age range change
  const handleAgeRangeChange = (event, newValue) => {
    onDeepNestedChange('targetAudience', 'demographics', 'ageRange', {
      min: newValue[0],
      max: newValue[1]
    });
  };

  // Handle gender change
  const handleGenderChange = (event) => {
    onDeepNestedChange('targetAudience', 'demographics', 'gender', event.target.value);
  };

  // Handle locations change
  const handleLocationsChange = (event, newValue) => {
    onDeepNestedChange('targetAudience', 'demographics', 'locations', newValue);
  };

  // Handle languages change
  const handleLanguagesChange = (event, newValue) => {
    onDeepNestedChange('targetAudience', 'demographics', 'languages', newValue);
  };

  // Handle interests change
  const handleInterestsChange = (event, newValue) => {
    onDeepNestedChange('targetAudience', 'psychographics', 'interests', newValue);
  };

  // Handle behaviors change
  const handleBehaviorsChange = (event, newValue) => {
    onDeepNestedChange('targetAudience', 'psychographics', 'behaviors', newValue);
  };

  // Handle values change
  const handleValuesChange = (event, newValue) => {
    onDeepNestedChange('targetAudience', 'psychographics', 'values', newValue);
  };

  // Handle lifestyle factors change
  const handleLifestyleFactorsChange = (event, newValue) => {
    onDeepNestedChange('targetAudience', 'psychographics', 'lifestyleFactors', newValue);
  };

  // Primary Demographics section renderer
  const renderPrimaryDemographics = () => (
    <Grid container spacing={3}>
      {/* Age Range */}
      <Grid item xs={12} md={6}>
        <Typography variant="subtitle2" gutterBottom>
          Age Range: {targetAudience.demographics?.ageRange?.min || 18} - {targetAudience.demographics?.ageRange?.max || 65}
        </Typography>
        <Slider
          value={[
            targetAudience.demographics?.ageRange?.min || 18,
            targetAudience.demographics?.ageRange?.max || 65
          ]}
          onChange={handleAgeRangeChange}
          valueLabelDisplay="auto"
          min={13}
          max={65}
          step={1}
          sx={{ mt: 2 }}
        />
      </Grid>

      {/* Gender */}
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel 
            id="gender-label"
            error={touched?.['targetAudience.demographics.gender'] && Boolean(errors?.['targetAudience.demographics.gender'])}
          >
            Gender
          </InputLabel>
          <Select
            labelId="gender-label"
            id="gender"
            value={targetAudience.demographics?.gender || 'all'}
            onChange={handleGenderChange}
            label="Gender"
            error={touched?.['targetAudience.demographics.gender'] && Boolean(errors?.['targetAudience.demographics.gender'])}
          >
            {GENDER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );

  // Geographic & Language section renderer
  const renderGeographicLanguage = () => (
    <Grid container spacing={3}>
      {/* Locations */}
      <Grid item xs={12} md={6}>
        <Autocomplete
          multiple
          id="locations"
          options={[]}
          freeSolo
          value={targetAudience.demographics?.locations || []}
          onChange={handleLocationsChange}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                icon={<LocationIcon />}
                label={option}
                {...getTagProps({ index })}
                variant="outlined"
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Target Locations *" // Add asterisk to show it's required
              placeholder="Add location and press Enter"
              fullWidth
              required
              error={touched?.['targetAudience.demographics.locations'] && Boolean(errors?.['targetAudience.demographics.locations'])}
              helperText={
                touched?.['targetAudience.demographics.locations'] && errors?.['targetAudience.demographics.locations'] 
                  ? errors['targetAudience.demographics.locations'] 
                  : "Cities, countries, or regions (required)"
              }
              InputLabelProps={{
                error: touched?.['targetAudience.demographics.locations'] && Boolean(errors?.['targetAudience.demographics.locations']),
                required: true,
              }}
            />
          )}
        />
      </Grid>

      {/* Languages */}
      <Grid item xs={12} md={6}>
        <Autocomplete
          multiple
          id="languages"
          options={LANGUAGE_OPTIONS}
          getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
          value={targetAudience.demographics?.languages || []}
          onChange={handleLanguagesChange}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                icon={<LanguageIcon />}
                label={typeof option === 'string' ? option : option.label}
                {...getTagProps({ index })}
                variant="outlined"
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Languages"
              placeholder="Select languages"
              fullWidth
              error={touched?.['targetAudience.demographics.languages'] && Boolean(errors?.['targetAudience.demographics.languages'])}
              helperText={
                touched?.['targetAudience.demographics.languages'] && errors?.['targetAudience.demographics.languages'] 
                  ? errors['targetAudience.demographics.languages'] 
                  : ""
              }
              InputLabelProps={{
                error: touched?.['targetAudience.demographics.languages'] && Boolean(errors?.['targetAudience.demographics.languages']),
              }}
            />
          )}
        />
      </Grid>
    </Grid>
  );

  // Psychographics section renderer
  const renderPsychographics = () => (
    <Grid container spacing={3}>
      {/* Interests */}
      <Grid item xs={12} md={6}>
        <Autocomplete
          multiple
          id="interests"
          options={INTEREST_SUGGESTIONS}
          freeSolo
          value={targetAudience.psychographics?.interests || []}
          onChange={handleInterestsChange}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                icon={<InterestsIcon />}
                label={option}
                {...getTagProps({ index })}
                variant="outlined"
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Interests"
              placeholder="Add interests"
              fullWidth
              helperText="Topics and activities the audience is interested in"
            />
          )}
        />
      </Grid>

      {/* Behaviors */}
      <Grid item xs={12} md={6}>
        <Autocomplete
          multiple
          id="behaviors"
          options={BEHAVIOR_SUGGESTIONS}
          freeSolo
          value={targetAudience.psychographics?.behaviors || []}
          onChange={handleBehaviorsChange}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                icon={<BehaviorIcon />}
                label={option}
                {...getTagProps({ index })}
                color="info"
                variant="outlined"
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Behaviors"
              placeholder="Add behaviors"
              fullWidth
              helperText="How the audience typically behaves as consumers"
            />
          )}
        />
      </Grid>

      {/* Values */}
      <Grid item xs={12} md={6}>
        <Autocomplete
          multiple
          id="values"
          options={VALUES_SUGGESTIONS}
          freeSolo
          value={targetAudience.psychographics?.values || []}
          onChange={handleValuesChange}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                icon={<ValuesIcon />}
                label={option}
                {...getTagProps({ index })}
                color="secondary"
                variant="outlined"
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Values"
              placeholder="Add values"
              fullWidth
              helperText="What the audience believes in and values"
            />
          )}
        />
      </Grid>

      {/* Lifestyle Factors */}
      <Grid item xs={12} md={6}>
        <Autocomplete
          multiple
          id="lifestyleFactors"
          options={LIFESTYLE_SUGGESTIONS}
          freeSolo
          value={targetAudience.psychographics?.lifestyleFactors || []}
          onChange={handleLifestyleFactorsChange}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                icon={<LifestyleIcon />}
                label={option}
                {...getTagProps({ index })}
                variant="outlined"
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Lifestyle Factors"
              placeholder="Add lifestyle factors"
              fullWidth
              helperText="How the audience lives and what influences their decisions"
            />
          )}
        />
      </Grid>
    </Grid>
  );

  // Tips section renderer
  const renderTargetingTips = () => (
    <Box sx={{ 
      p: 2, 
      bgcolor: alpha(theme.palette.info.main, 0.1), 
      borderRadius: 1,
      mt: 2 
    }}>
      <Typography variant="subtitle2" gutterBottom>
        Tips for Effective Audience Targeting:
      </Typography>
      <Typography variant="body2">
        • Be specific about who you're targeting to improve campaign effectiveness
      </Typography>
      <Typography variant="body2">
        • Consider both demographic data and psychographic insights
      </Typography>
      <Typography variant="body2">
        • Think about your audience's pain points and how your campaign addresses them
      </Typography>
      <Typography variant="body2">
        • For better results, align audience targeting with the platforms you've selected
      </Typography>
    </Box>
  );

  return (
    <>
      {/* Section 1: Primary Demographics */}
      <FormSection
        title="Primary Demographics"
        description="Define the core demographic characteristics of your target audience."
        icon={<PeopleIcon />}
        tooltip="Basic demographic data helps narrow your audience to the right age groups and genders."
      >
        {renderPrimaryDemographics()}
      </FormSection>

      {/* Section 2: Geographic & Language Targeting */}
      <FormSection
        title="Geographic & Language Targeting"
        description="Specify where your audience is located and what languages they speak."
        icon={<LocationIcon />}
        tooltip="Target specific regions, countries, cities, and language preferences."
      >
        {renderGeographicLanguage()}
      </FormSection>

      {/* Section 3: Audience Psychographics */}
      <FormSection
        title="Audience Psychographics"
        description="Define psychological characteristics, interests, and behaviors of your audience."
        icon={<PsychologyIcon />}
        tooltip="Psychographics helps you understand the audience's lifestyle, interests, and values."
      >
        {renderPsychographics()}
      </FormSection>

      {/* Section 4: Tips */}
      <FormSection
        title="Targeting Best Practices"
        description="Recommendations for more effective audience targeting."
        icon={<SegmentationIcon />}
        tooltip="Tips to help you define your audience more effectively for better campaign results."
      >
        {renderTargetingTips()}
      </FormSection>
    </>
  );
};

export default CampaignTargetAudienceForm;